// ============================================
// Chat Module - Parser (ES6)
// AI 메시지 파싱 및 포맷팅
// ============================================

import { getCurrentChatData, getCurrentProfile } from '../app/state.js';
import { preprocessAIContent, normalizeBlockContent, formatDialogueText } from './messaging/formatter.js';
import { escapeHtml } from '../utils.js';

// ============================================
// 상수
// ============================================

// 모든 마커 패턴 (새 시스템) - I: 이미지 마커, U: 유저 대사, S: 상황별 이미지
// 감정 수정자 지원: {C:happy}, {N:dramatic} 등
export const MARKER_REGEX = /\{(N|C\d*|T\d*|H|I|S\d*|U|X\/[^/]+\/\??)(?::[a-zA-Z]+)?\}/g;

// ============================================
// 유틸리티 함수
// ============================================

// 구 시스템 닫는 태그 제거 (하위 호환)
export function removeOldClosingTags(text) {
  return text.replace(/\{\/[NCTH]\}/g, '');
}

// 마커 타입과 내용 추출
export function parseMarkerType(marker) {
  if (marker === 'N') return { type: 'narration' };
  if (marker === 'H') return { type: 'helper' };
  if (marker === 'I') return { type: 'image' };  // 인트로 이미지 마커
  if (marker === 'S') return { type: 'situational', index: -1 };  // 상황별 이미지 마커
  if (/^S\d+$/.test(marker)) return { type: 'situational', index: parseInt(marker.slice(1)) };
  if (marker === 'U') return { type: 'user' };   // 유저 대사 인용
  if (marker === 'C') return { type: 'dialogue', index: -1 }; // 캐릭터 모드 메인 캐릭터
  if (marker === 'T') return { type: 'thought', index: -1 }; // 캐릭터 모드 메인 캐릭터 생각
  if (/^C\d+$/.test(marker)) return { type: 'dialogue', index: parseInt(marker.slice(1)) };
  if (/^T\d+$/.test(marker)) return { type: 'thought', index: parseInt(marker.slice(1)) };
  if (/^X\/(.+)\/\??$/.test(marker)) {
    const match = marker.match(/^X\/(.+)\/\??$/);
    return { type: 'extra', name: match[1] };
  }
  return { type: 'unknown' };
}

// 캐릭터 인덱스로 이름 가져오기
export function getCharacterNameByIndex(index) {
  const currentChatData = getCurrentChatData();
  if (index === -1) {
    // 캐릭터 모드의 메인 캐릭터
    return currentChatData?.character?.name || '점술가';
  }
  if (currentChatData?.characters && currentChatData.characters[index]) {
    return currentChatData.characters[index].name;
  }
  return `점술가${index}`;
}

// 현재 채팅의 캐릭터 이름 가져오기
export function getCurrentCharacterName() {
  const currentChatData = getCurrentChatData();
  if (currentChatData) {
    // 캐릭터 모드
    if (currentChatData.character?.name) {
      return currentChatData.character.name;
    }
    // 스토리 모드 - 첫 번째 캐릭터 이름 (기본값)
    if (currentChatData.characters?.length > 0) {
      return currentChatData.characters[0].name;
    }
  }
  return '상대방';
}

// 현재 채팅의 모든 캐릭터 이름 목록
export function getCharacterNames() {
  const currentChatData = getCurrentChatData();
  if (currentChatData) {
    if (currentChatData.character?.name) {
      return [currentChatData.character.name];
    }
    if (currentChatData.characters?.length > 0) {
      return currentChatData.characters.filter(c => c).map(c => c.name);
    }
  }
  return [];
}

// ============================================
// 파싱 함수
// ============================================

// 연속된 나레이션 블록 병합 (가독성 향상)
export function mergeConsecutiveNarrations(blocks) {
  const merged = [];
  for (const block of blocks) {
    const prev = merged[merged.length - 1];
    if (prev?.type === 'narration' && block.type === 'narration') {
      // 연속 나레이션 → 줄바꿈으로 연결
      prev.content += '\n\n' + block.content;
    } else {
      merged.push(block);
    }
  }
  return merged;
}

// 구 시스템 *...* 나레이션 파싱 (하위 호환)
export function parseOldAsteriskFormat(text) {
  const blocks = [];
  const parts = text.split(/(\*[^*]+\*)/g);

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    if (/^\*[^*]+\*$/.test(trimmed)) {
      // *...* 나레이션
      blocks.push({ type: 'narration', content: trimmed.slice(1, -1) });
    } else {
      // 일반 텍스트 - 대사로 처리
      const dialogueMatch = trimmed.match(/^([가-힣a-zA-Z]+)\s*[:：]\s*(.*)$/);
      if (dialogueMatch) {
        blocks.push({ type: 'dialogue', speaker: dialogueMatch[1], content: dialogueMatch[2] || '' });
      } else {
        blocks.push({ type: 'dialogue', speaker: getCurrentCharacterName(), content: trimmed });
      }
    }
  }

  return mergeConsecutiveNarrations(blocks);
}

// 새 마커 시스템 파싱 (스트리밍/최종 공용)
export function parseNewMarkerSystem(text) {
  const blocks = [];

  // 구 시스템 닫는 태그 제거
  text = removeOldClosingTags(text);

  // 메타 질문 패턴 제거
  const metaPatterns = [
    /어떤 화제를 꺼내시겠습니까\??/g,
    /어떻게 하시겠습니까\??/g,
    /어떻게 반응하시겠습니까\??/g,
    /무엇을 하시겠습니까\??/g,
    /어떤 선택을 하시겠습니까\??/g,
    /당신의 선택은\??/g,
    /어떤 행동을 취하시겠습니까\??/g,
    /다음 행동을 선택하세요\.?/g,
    /(?:^|\n)\s*[1-9]\.\s*[""]?[^"""\n]+[""]?\s*(\([^)]+\))?/gm,
  ];
  for (const pattern of metaPatterns) {
    text = text.replace(pattern, '');
  }

  // 마커 위치 찾기 (감정 수정자 포함: {C:happy}, {N:dramatic} 등)
  const markers = [];
  let match;
  const regex = /\{(N|C\d*|T\d*|H|I|S\d*|U|X\/[^/]+\/)(?::[a-zA-Z]+)?\}/g;

  while ((match = regex.exec(text)) !== null) {
    markers.push({
      marker: match[1],
      index: match.index,
      fullMatch: match[0]
    });
  }

  // 마커가 없으면 전체를 plain으로
  if (markers.length === 0) {
    const trimmed = text.trim();
    if (trimmed) {
      // 구 시스템 *...* 나레이션 처리 (하위 호환)
      if (trimmed.includes('*')) {
        return parseOldAsteriskFormat(trimmed);
      }
      blocks.push({ type: 'plain', content: trimmed });
    }
    return blocks;
  }

  // 첫 마커 이전 텍스트 처리
  if (markers[0].index > 0) {
    const beforeText = text.slice(0, markers[0].index).trim();
    if (beforeText) {
      blocks.push({ type: 'plain', content: beforeText });
    }
  }

  // 각 마커 처리
  for (let i = 0; i < markers.length; i++) {
    const current = markers[i];
    const next = markers[i + 1];

    const contentStart = current.index + current.fullMatch.length;
    const contentEnd = next ? next.index : text.length;
    const content = text.slice(contentStart, contentEnd).trim();

    const parsed = parseMarkerType(current.marker);

    // content가 비어있으면 건너뛰기 (단, situational/image는 마커에 인덱스가 있으면 처리)
    if (!content && !(parsed.type === 'situational' && parsed.index >= 0) && !(parsed.type === 'image')) {
      continue;
    }

    if (parsed.type === 'narration') {
      blocks.push({ type: 'narration', content });
    } else if (parsed.type === 'helper') {
      blocks.push({ type: 'helper', content });
    } else if (parsed.type === 'image') {
      // 인트로 이미지 블록 - content는 이미지 인덱스
      const imageIndex = content.split('\n')[0].trim();
      if (imageIndex) {
        blocks.push({ type: 'image', content: imageIndex });
      }
    } else if (parsed.type === 'situational') {
      // parsed.index 우선 사용 ({S0} 형식), 없으면 content에서 추출 ({S}0 형식)
      let imageIndex;
      if (parsed.index >= 0) {
        imageIndex = String(parsed.index);
      } else {
        // {S}0 형식 - content 앞의 숫자만 추출
        const match = content.match(/^(\d+)/);
        imageIndex = match ? match[1] : '';
      }
      if (imageIndex !== '') {
        blocks.push({ type: 'situational', content: imageIndex });
      }
    } else if (parsed.type === 'dialogue') {
      const speaker = getCharacterNameByIndex(parsed.index);
      blocks.push({ type: 'dialogue', speaker, content });
    } else if (parsed.type === 'thought') {
      const speaker = getCharacterNameByIndex(parsed.index);
      blocks.push({ type: 'thought', speaker, content });
    } else if (parsed.type === 'extra') {
      blocks.push({ type: 'dialogue', speaker: parsed.name, content, isExtra: true });
    } else if (parsed.type === 'user') {
      // 유저 대사 인용 - 유저 메시지처럼 오른쪽에 표시
      blocks.push({ type: 'user-quote', content });
    }
  }

  return mergeConsecutiveNarrations(blocks);
}

// 스트리밍용 파싱 - 새 마커 시스템 사용
export function parseAIContentStreaming(text) {
  return parseNewMarkerSystem(text);
}

// 최종 메시지용 파싱
export function parseAIContent(text) {
  return parseNewMarkerSystem(text);
}

// ============================================
// 아바타 함수
// ============================================

export function getCharacterAvatar(speakerName) {
  const currentChatData = getCurrentChatData();
  // 캐릭터 데이터에서 이미지 URL 또는 성별 확인
  if (currentChatData) {
    // 캐릭터 모드
    if (currentChatData.character?.name === speakerName) {
      if (currentChatData.character.imageUrl) {
        return { type: 'image', url: currentChatData.character.imageUrl, name: speakerName };
      }
      return { type: 'emoji', emoji: currentChatData.character.gender === '여성' ? '👩' : '👨' };
    }
    // 스토리 모드
    if (currentChatData.characters) {
      const char = currentChatData.characters.find(c => c && c.name === speakerName);
      if (char) {
        // 이미지 URL 확인 (imageUrl 또는 images 배열)
        const charImgUrl = char.imageUrl || char.images?.[0]?.url || char.images?.[0];
        if (charImgUrl && typeof charImgUrl === 'string') {
          return { type: 'image', url: charImgUrl, name: speakerName };
        }
        return { type: 'emoji', emoji: char.gender === '여성' ? '👩' : '👨' };
      }
    }
  }
  // 미등록 캐릭터 (윤하린 아버지 등 새로 등장한 인물) - 회색 아바타
  return { type: 'extra' };
}

export function renderAvatarHTML(avatarData) {
  if (avatarData.type === 'image') {
    // 이미지가 이미 캐시되어 있는지 확인
    const testImg = new Image();
    testImg.src = avatarData.url;
    const isCached = testImg.complete && testImg.naturalHeight !== 0;
    const loadedClass = isCached ? ' loaded' : '';
    return `<div class="img-loading-wrapper dialogue-avatar-wrapper${loadedClass}"><div class="spinner"></div><img src="${avatarData.url}" alt="${avatarData.name}" class="dialogue-avatar-img${loadedClass}" data-action="showImage" data-url="${avatarData.url}" data-name="${avatarData.name}" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')"></div>`;
  }
  // 추가 캐릭터 (이미지 없음) - 회색 배경에 사람 아이콘
  if (avatarData.type === 'extra') {
    return `<div class="extra-character-avatar"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></div>`;
  }
  return avatarData.emoji;
}

// ============================================
// 포맷팅 함수
// ============================================

export function formatAIMessage(content) {
  const currentChatData = getCurrentChatData();
  const currentProfile = getCurrentProfile();

  // 공용 전처리 (메모리 마커, 이중 중괄호 제거)
  let processed = preprocessAIContent(content, false);

  // {{user}} 마커 처리 - 프로필이 있으면 이름으로, 없으면 플레이스홀더
  const userName = currentChatData?.userProfile?.name || currentProfile?.name || null;
  if (userName) {
    processed = processed.replace(/\{\{user\}\}/gi, userName);
  } else {
    processed = processed.replace(/\{\{user\}\}/gi, '{{USER_PLACEHOLDER}}');
  }

  // HTML 이스케이프
  let escaped = escapeHtml(processed.trim());
  // 플레이스홀더를 보라색 스타일의 {{user}}로 변환
  escaped = escaped.replace(/\{\{USER_PLACEHOLDER\}\}/g, '<span class="user-var-placeholder">{{user}}</span>');

  // 콘텐츠를 파싱해서 나레이션과 대사로 분리
  const blocks = parseAIContent(escaped);

  return blocks.map(block => {
    let innerHTML;
    const normalizedContent = normalizeBlockContent(block.content);
    const formattedContent = formatDialogueText(normalizedContent);
    if (block.type === 'narration') {
      innerHTML = `<div class="narration-block">${formattedContent}</div>`;
    } else if (block.type === 'image') {
      const imageIndex = parseInt(block.content.trim(), 10);
      const imageUrl = !isNaN(imageIndex) ? (currentChatData?.introImages?.[imageIndex] || '') : '';
      if (imageUrl) {
        innerHTML = `<div class="intro-image-block-chat"><img src="${imageUrl}" alt="이미지" data-action="showImage" data-url="${imageUrl}" data-name="이미지" onerror="this.parentElement&&(this.parentElement.style.display='none')"></div>`;
      } else {
        innerHTML = '';
      }
    } else if (block.type === 'situational') {
      // 상황별 이미지 렌더링
      const imageIndex = parseInt(block.content.trim(), 10);
      const situationalImages = currentChatData?.situationalImages || [];
      const imageData = !isNaN(imageIndex) ? situationalImages[imageIndex] : null;
      if (imageData?.imageUrl) {
        const altText = escapeHtml(imageData.description || '상황 이미지');
        innerHTML = `<div class="situational-image-block-chat"><img src="${imageData.imageUrl}" alt="${altText}" data-action="showImage" data-url="${imageData.imageUrl}" data-name="${altText}" onerror="this.parentElement&&(this.parentElement.style.display='none')"></div>`;
      } else {
        innerHTML = '';
      }
    } else if (block.type === 'helper') {
      innerHTML = `<div class="helper-block"><div class="helper-avatar">🌟</div><div class="helper-content"><div class="helper-speaker">별님이</div><div class="helper-bubble"><div class="helper-text">${formattedContent}</div></div></div></div>`;
    } else if (block.type === 'thought') {
      const avatarData = block.isExtra ? { type: 'extra' } : getCharacterAvatar(block.speaker);
      const avatarHTML = renderAvatarHTML(avatarData);
      innerHTML = `<div class="thought-block"><div class="thought-avatar">${avatarHTML}</div><div class="thought-content"><div class="thought-speaker">${escapeHtml(block.speaker)}</div><div class="thought-bubble"><div class="thought-text">${formattedContent}</div></div></div></div>`;
    } else if (block.type === 'dialogue') {
      const avatarData = block.isExtra ? { type: 'extra' } : getCharacterAvatar(block.speaker);
      const avatarHTML = renderAvatarHTML(avatarData);
      innerHTML = `<div class="dialogue-block"><div class="dialogue-avatar">${avatarHTML}</div><div class="dialogue-content"><div class="dialogue-speaker">${escapeHtml(block.speaker)}</div><div class="dialogue-bubble"><div class="dialogue-text">${formatDialogueText(block.content)}</div></div></div></div>`;
    } else if (block.type === 'user-quote') {
      // 유저 대사 인용 - AI 메시지 안에서 오른쪽 정렬로 표시
      const formattedQuote = formatDialogueText(normalizedContent);
      return `<div class="ai-content-block user-quote-wrapper"><div class="user-quote-block"><div class="user-quote-content">${formattedQuote}</div></div></div>`;
    } else {
      innerHTML = `<div class="plain-text-block">${formattedContent}</div>`;
    }
    return `<div class="ai-content-block">${innerHTML}</div>`;
  }).join('');
}
